import React from 'react';

interface SplitItemsStepProps {
  splitData: any;
  splitItems: { [key: string]: { [key: string]: string } };
  setSplitItems: React.Dispatch<React.SetStateAction<{ [key: string]: { [key: string]: string } }>>;
  splitModeRatio: { [key: string]: boolean };
  setSplitModeRatio: React.Dispatch<React.SetStateAction<{ [key: string]: boolean }>>;
  tipAmount: string;
  setTipAmount: React.Dispatch<React.SetStateAction<string>>;
  tipSplit: { [key: string]: string };
  setTipSplit: React.Dispatch<React.SetStateAction<{ [key: string]: string }>>;
  tipRatio: boolean;
  setTipRatio: React.Dispatch<React.SetStateAction<boolean>>;
  payerPersonOrder: number;
  setPayerPersonOrder: React.Dispatch<React.SetStateAction<number>>;
  contactPersonOrder: number;
  setContactPersonOrder: React.Dispatch<React.SetStateAction<number>>;
  isSplitProcessing: boolean;
  splitStatusMessage: string;
  errorMessage: string;
  onProcessSplit: () => void;
  onBackToPeople: () => void;
}

const SplitItemsStep: React.FC<SplitItemsStepProps> = ({
  splitData,
  splitItems,
  setSplitItems,
  splitModeRatio,
  setSplitModeRatio,
  tipAmount,
  setTipAmount,
  tipSplit,
  setTipSplit,
  tipRatio,
  setTipRatio,
  payerPersonOrder,
  setPayerPersonOrder,
  contactPersonOrder,
  setContactPersonOrder,
  isSplitProcessing,
  splitStatusMessage,
  errorMessage,
  onProcessSplit,
  onBackToPeople
}) => {
  const people = splitData.people || [];
  const items = splitData.items || [];

  return (
    <div style={{
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      background: '#667eea',
      padding: '20px',
      minHeight: '100vh'
    }}>
      <div style={{
        width: 'min(900px, 100%)',
        margin: '0 auto',
        background: '#fff',
        borderRadius: '14px',
        padding: 'clamp(16px, 4vw, 28px)',
        boxShadow: '0 4px 12px rgba(0,0,0,.08)'
      }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '22px' }}>
          <div style={{
            width: '60px',
            height: '60px',
            borderRadius: '50%',
            background: '#fb923c',
            display: 'grid',
            placeItems: 'center',
            margin: '0 auto 14px'
          }}>
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" style={{ width: '30px', height: '30px', stroke: '#fff', strokeWidth: 2, fill: 'none' }}>
              <path d="M9 12l2 2 4-4"/>
              <path d="M21 12c-1 0-3-1-3-3s2-3 3-3 3 1 3 3-2 3-3 3"/>
              <path d="M3 12c1 0 3-1 3-3s-2-3-3-3-3 1-3 3 2 3 3 3"/>
              <path d="M13 12h3"/>
              <path d="M8 12H5"/>
            </svg>
          </div>
          <h1 style={{ fontSize: 'clamp(22px, 6vw, 30px)', margin: '0 0 6px', color: '#111827' }}>
            Rozdeľte položky
          </h1>
          <p style={{ color: '#6b7280', fontSize: 'clamp(15px, 4vw, 17px)' }}>
            Pre každú položku nastavte komu a ako ju prerozdeliť. Ak ponecháte režim ks rozdelí sa položka po kusoch (cena za ks) pre vybrané osoby. Ak zmeníte režim na podiel, celková cena za položku sa rozdelí podľa pomeru, ktorý ste uviedli pre vybrané osoby. Napríklad fľašu vína k večeri môžete rozdeliť podielovo v pomere 1:1:1 pre tri osoby, každá osoba zaplatí 1/3 ceny za víno.
          </p>
        </div>

        {/* Receipt Info */}
        <div style={{
          background: '#f0fdf4',
          border: '1px solid #bbf7d0',
          borderRadius: '12px',
          padding: '16px',
          marginBottom: '20px'
        }}>
          <h3 style={{ margin: '0 0 8px', color: '#166534' }}>Informácie o bločku</h3>
          <p style={{ margin: '0', fontSize: '14px', color: '#065f46' }}>
            <strong>{splitData.receipt?.merchant}</strong> • {splitData.receipt?.total_gross} EUR • {splitData.receipt?.datetime ? new Date(splitData.receipt.datetime).toLocaleDateString('sk-SK') : ''}
          </p>
        </div>

        {/* Items - Mobile Card Layout & Desktop Table */}
        <div style={{ marginBottom: '20px' }}>
          {/* Mobile Card Layout */}
          <div style={{ display: 'block' }} className="mobile-cards">
            {items.map((item: any) => (
              <div key={item.item_id} style={{
                background: '#f9fafb',
                border: '1px solid #e5e7eb',
                borderRadius: '12px',
                padding: '16px',
                marginBottom: '16px'
              }}>
                {/* Item Header */}
                <div style={{ marginBottom: '12px' }}>
                  <div style={{ fontWeight: 600, fontSize: '16px', marginBottom: '4px' }}>
                    {item.raw_name}
                  </div>
                  <div style={{ fontSize: '14px', color: '#6b7280' }}>
                    {item.qty} × {(parseFloat(item.total_gross) / parseFloat(item.qty)).toFixed(2)} € = <strong>{parseFloat(item.total_gross).toFixed(2)} €</strong>
                  </div>
                </div>

                {/* Mode Toggle */}
                <div style={{ marginBottom: '16px', paddingBottom: '12px', borderBottom: '1px solid #e5e7eb' }}>
                  <div style={{ fontSize: '13px', color: '#6b7280', marginBottom: '8px' }}>Režim rozdelenia:</div>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      type="button"
                      onClick={() => setSplitModeRatio({
                        ...splitModeRatio,
                        [item.item_id]: false
                      })}
                      style={{
                        flex: 1,
                        padding: '10px',
                        border: splitModeRatio[item.item_id] ? '1px solid #d1d5db' : '2px solid #667eea',
                        borderRadius: '8px',
                        background: splitModeRatio[item.item_id] ? '#fff' : '#ede9fe',
                        color: splitModeRatio[item.item_id] ? '#6b7280' : '#667eea',
                        fontWeight: splitModeRatio[item.item_id] ? 'normal' : 'bold',
                        fontSize: '14px',
                        cursor: 'pointer'
                      }}
                    >
                      ks
                    </button>
                    <button
                      type="button"
                      onClick={() => setSplitModeRatio({
                        ...splitModeRatio,
                        [item.item_id]: true
                      })}
                      style={{
                        flex: 1,
                        padding: '10px',
                        border: splitModeRatio[item.item_id] ? '2px solid #667eea' : '1px solid #d1d5db',
                        borderRadius: '8px',
                        background: splitModeRatio[item.item_id] ? '#ede9fe' : '#fff',
                        color: splitModeRatio[item.item_id] ? '#667eea' : '#6b7280',
                        fontWeight: splitModeRatio[item.item_id] ? 'bold' : 'normal',
                        fontSize: '14px',
                        cursor: 'pointer'
                      }}
                    >
                      Pomer
                    </button>
                  </div>
                </div>

                {/* Person Inputs */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {people.map((person: any) => {
                    const personKey = `P${person.person_order}`;
                    const currentValue = parseFloat(splitItems[item.item_id]?.[personKey] || '0');

                    const handleIncrement = () => {
                      const newSplitItems = { ...splitItems };
                      if (!newSplitItems[item.item_id]) {
                        newSplitItems[item.item_id] = {};
                      }
                      newSplitItems[item.item_id][personKey] = String(currentValue + 1);
                      setSplitItems(newSplitItems);
                    };

                    const handleDecrement = () => {
                      if (currentValue > 0) {
                        const newSplitItems = { ...splitItems };
                        if (!newSplitItems[item.item_id]) {
                          newSplitItems[item.item_id] = {};
                        }
                        newSplitItems[item.item_id][personKey] = String(currentValue - 1);
                        setSplitItems(newSplitItems);
                      }
                    };

                    return (
                      <div key={person.person_order} style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: '12px'
                      }}>
                        <div
                          style={{
                            fontWeight: 500,
                            fontSize: '15px',
                            minWidth: '80px',
                            maxWidth: '140px',
                            flex: '0 1 auto',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            wordBreak: 'break-word',
                            lineHeight: '1.3'
                          }}
                          title={person.person_name}
                        >
                          {person.person_name}
                        </div>
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          flex: '1',
                          justifyContent: 'flex-end'
                        }}>
                          <button
                            type="button"
                            onClick={handleDecrement}
                            disabled={currentValue <= 0}
                            style={{
                              width: '40px',
                              height: '40px',
                              border: '1px solid #d1d5db',
                              borderRadius: '8px',
                              background: currentValue <= 0 ? '#f3f4f6' : '#fff',
                              color: currentValue <= 0 ? '#9ca3af' : '#374151',
                              fontSize: '20px',
                              fontWeight: 'bold',
                              cursor: currentValue <= 0 ? 'not-allowed' : 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              padding: 0
                            }}
                          >
                            −
                          </button>
                          <input
                            type="text"
                            inputMode="decimal"
                            pattern="[0-9]*\.?[0-9]*"
                            placeholder="0"
                            value={splitItems[item.item_id]?.[personKey] || ''}
                            onChange={(e) => {
                              const value = e.target.value;
                              // Allow empty, numbers, and decimal point
                              if (value === '' || /^\d*\.?\d*$/.test(value)) {
                                const newSplitItems = { ...splitItems };
                                if (!newSplitItems[item.item_id]) {
                                  newSplitItems[item.item_id] = {};
                                }
                                newSplitItems[item.item_id][personKey] = value;
                                setSplitItems(newSplitItems);
                              }
                            }}
                            onBlur={(e) => {
                              // Ensure valid number on blur
                              const value = e.target.value;
                              if (value !== '' && !isNaN(parseFloat(value))) {
                                const numValue = Math.max(0, parseFloat(value));
                                const newSplitItems = { ...splitItems };
                                if (!newSplitItems[item.item_id]) {
                                  newSplitItems[item.item_id] = {};
                                }
                                newSplitItems[item.item_id][personKey] = String(numValue);
                                setSplitItems(newSplitItems);
                              }
                            }}
                            style={{
                              width: '70px',
                              height: '40px',
                              padding: '8px',
                              border: '1px solid #d1d5db',
                              borderRadius: '8px',
                              fontSize: '16px',
                              textAlign: 'center',
                              fontWeight: '500'
                            }}
                          />
                          <button
                            type="button"
                            onClick={handleIncrement}
                            style={{
                              width: '40px',
                              height: '40px',
                              border: '1px solid #d1d5db',
                              borderRadius: '8px',
                              background: '#fff',
                              color: '#374151',
                              fontSize: '20px',
                              fontWeight: 'bold',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              padding: 0
                            }}
                          >
                            +
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          {/* Desktop Table - Hidden on Mobile */}
          <style>{`
            @media (min-width: 768px) {
              .mobile-cards {
                display: none !important;
              }
              .desktop-table {
                display: block !important;
              }
            }
            @media (max-width: 767px) {
              .desktop-table {
                display: none !important;
              }
            }
          `}</style>
          <div style={{ overflowX: 'auto', display: 'none' }} className="desktop-table">
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
              <thead>
                <tr style={{ background: '#f9fafb' }}>
                  <th style={{ padding: '12px 8px', textAlign: 'left', border: '1px solid #e5e7eb' }}>Položka</th>
                  <th style={{ padding: '12px 8px', textAlign: 'center', border: '1px solid #e5e7eb' }}>Cena celkovo</th>
                  <th style={{ padding: '12px 8px', textAlign: 'center', border: '1px solid #e5e7eb' }}>ks</th>
                  <th style={{ padding: '12px 8px', textAlign: 'center', border: '1px solid #e5e7eb' }}>Režim</th>
                  {people.map((person: any) => (
                    <th key={person.person_order} style={{
                      padding: '12px 8px',
                      textAlign: 'center',
                      border: '1px solid #e5e7eb',
                      minWidth: '100px',
                      maxWidth: '150px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}
                    title={person.person_name}>
                      {person.person_name}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {items.map((item: any) => (
                  <tr key={item.item_id}>
                    <td style={{ padding: '8px', border: '1px solid #e5e7eb' }}>
                      <div style={{ fontWeight: 600 }}>{item.raw_name}</div>
                      <div style={{ fontSize: '12px', color: '#6b7280' }}>
                        Cena/ks: {(parseFloat(item.total_gross) / parseFloat(item.qty)).toFixed(2)} €
                      </div>
                    </td>
                    <td style={{ padding: '8px', border: '1px solid #e5e7eb', textAlign: 'center' }}>
                      {parseFloat(item.total_gross).toFixed(2)} €
                    </td>
                    <td style={{ padding: '8px', border: '1px solid #e5e7eb', textAlign: 'center' }}>
                      {item.qty}
                    </td>
                    <td style={{ padding: '8px', border: '1px solid #e5e7eb', textAlign: 'center' }}>
                      <select
                        value={splitModeRatio[item.item_id] ? 'ratio' : 'qty'}
                        onChange={(e) => setSplitModeRatio({
                          ...splitModeRatio,
                          [item.item_id]: e.target.value === 'ratio'
                        })}
                        style={{
                          padding: '4px',
                          border: '1px solid #d1d5db',
                          borderRadius: '4px',
                          fontSize: '12px'
                        }}
                      >
                        <option value="qty">ks</option>
                        <option value="ratio">Pomer</option>
                      </select>
                    </td>
                    {people.map((person: any) => {
                      const personKey = `P${person.person_order}`;
                      const currentValue = parseFloat(splitItems[item.item_id]?.[personKey] || '0');

                      const handleIncrement = () => {
                        const newSplitItems = { ...splitItems };
                        if (!newSplitItems[item.item_id]) {
                          newSplitItems[item.item_id] = {};
                        }
                        newSplitItems[item.item_id][personKey] = String(currentValue + 1);
                        setSplitItems(newSplitItems);
                      };

                      const handleDecrement = () => {
                        if (currentValue > 0) {
                          const newSplitItems = { ...splitItems };
                          if (!newSplitItems[item.item_id]) {
                            newSplitItems[item.item_id] = {};
                          }
                          newSplitItems[item.item_id][personKey] = String(currentValue - 1);
                          setSplitItems(newSplitItems);
                        }
                      };

                      return (
                        <td key={person.person_order} style={{ padding: '8px', border: '1px solid #e5e7eb', textAlign: 'center' }}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                            <button
                              type="button"
                              onClick={handleDecrement}
                              disabled={currentValue <= 0}
                              style={{
                                width: '28px',
                                height: '28px',
                                border: '1px solid #d1d5db',
                                borderRadius: '4px',
                                background: currentValue <= 0 ? '#f3f4f6' : '#fff',
                                color: currentValue <= 0 ? '#9ca3af' : '#374151',
                                fontSize: '16px',
                                fontWeight: 'bold',
                                cursor: currentValue <= 0 ? 'not-allowed' : 'pointer',
                                padding: 0
                              }}
                            >
                              −
                            </button>
                            <input
                              type="text"
                              inputMode="decimal"
                              pattern="[0-9]*\.?[0-9]*"
                              placeholder="0"
                              value={splitItems[item.item_id]?.[personKey] || ''}
                              onChange={(e) => {
                                const value = e.target.value;
                                if (value === '' || /^\d*\.?\d*$/.test(value)) {
                                  const newSplitItems = { ...splitItems };
                                  if (!newSplitItems[item.item_id]) {
                                    newSplitItems[item.item_id] = {};
                                  }
                                  newSplitItems[item.item_id][personKey] = value;
                                  setSplitItems(newSplitItems);
                                }
                              }}
                              onBlur={(e) => {
                                const value = e.target.value;
                                if (value !== '' && !isNaN(parseFloat(value))) {
                                  const numValue = Math.max(0, parseFloat(value));
                                  const newSplitItems = { ...splitItems };
                                  if (!newSplitItems[item.item_id]) {
                                    newSplitItems[item.item_id] = {};
                                  }
                                  newSplitItems[item.item_id][personKey] = String(numValue);
                                  setSplitItems(newSplitItems);
                                }
                              }}
                              style={{
                                width: '50px',
                                padding: '4px',
                                border: '1px solid #d1d5db',
                                borderRadius: '4px',
                                fontSize: '13px',
                                textAlign: 'center'
                              }}
                            />
                            <button
                              type="button"
                              onClick={handleIncrement}
                              style={{
                                width: '28px',
                                height: '28px',
                                border: '1px solid #d1d5db',
                                borderRadius: '4px',
                                background: '#fff',
                                color: '#374151',
                                fontSize: '16px',
                                fontWeight: 'bold',
                                cursor: 'pointer',
                                padding: 0
                              }}
                            >
                              +
                            </button>
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Tip Section */}
        <div style={{
          background: '#fff7ed',
          border: '1px solid #fed7aa',
          borderRadius: '12px',
          padding: '16px',
          marginBottom: '20px'
        }}>
          <h3 style={{ margin: '0 0 12px', color: '#9a3412' }}>Tringelt (voliteľne)</h3>
          <p style={{ margin: '0 0 12px', color: '#9a3412', fontSize: '14px', lineHeight: '1.4' }}>
            Ak ste nechali nejaké prepitné (tringelt), môžete ho sem napísať a pomerovo rozdeliť medzi všetky osoby. Tringelt sa prerozdelí a dopočíta do rozúčtovania.
          </p>
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center', marginBottom: '12px' }}>
            <input
              type="number"
              step="0.01"
              min="0"
              placeholder="0.00"
              value={tipAmount}
              onChange={(e) => setTipAmount(e.target.value)}
              style={{
                padding: '8px 12px',
                border: '1px solid #fed7aa',
                borderRadius: '8px',
                fontSize: '16px',
                width: '100px'
              }}
            />
            <span>EUR</span>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginLeft: '20px' }}>
              <input
                type="checkbox"
                checked={tipRatio}
                onChange={(e) => setTipRatio(e.target.checked)}
              />
              Rozdeliť podľa pomeru
            </label>
          </div>

          {parseFloat(tipAmount) > 0 && (
            <div>
              <p style={{ margin: '0 0 8px', color: '#9a3412', fontSize: '12px' }}>
                {tipRatio
                  ? 'Zadajte pomery pre každú osobu (napr. 1, 1, 1 pre rovnaký podiel):'
                  : 'Zadajte konkrétnu sumu pre každú osobu (celkom musí byť ' + tipAmount + ' EUR):'
                }
              </p>
              <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                {people.map((person: any) => {
                  const personKey = `P${person.person_order}`;
                  return (
                    <div key={person.person_order} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span
                        style={{
                          fontSize: '14px',
                          minWidth: '80px',
                          maxWidth: '120px',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                        title={person.person_name}
                      >
                        {person.person_name}:
                      </span>
                      <input
                        type="number"
                        step={tipRatio ? "0.1" : "0.01"}
                        min="0"
                        placeholder={tipRatio ? "1" : "0.00"}
                        value={tipSplit[personKey] || ''}
                        onChange={(e) => {
                          setTipSplit({
                            ...tipSplit,
                            [personKey]: e.target.value
                          });
                        }}
                        style={{
                          width: '60px',
                          padding: '4px 8px',
                          border: '1px solid #fed7aa',
                          borderRadius: '4px',
                          fontSize: '14px'
                        }}
                      />
                      <span style={{ fontSize: '12px', color: '#9a3412' }}>
                        {tipRatio ? '' : 'EUR'}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Payer and Contact Selection */}
        <div style={{
          background: '#f0f9ff',
          border: '1px solid #bae6fd',
          borderRadius: '12px',
          padding: '16px',
          marginBottom: '20px'
        }}>
          <h3 style={{ margin: '0 0 12px', color: '#075985' }}>Kto platil a kontakt</h3>
          <p style={{ margin: '0 0 12px', color: '#075985', fontSize: '14px', lineHeight: '1.4' }}>
            Vyberte, kto účet platil a kto robí toto rozúčtovanie. Aby vaši priatelia alebo kolegovia vedeli koho kontaktovať ak je v rozúčtovaní chyba resp. komu svoj podiel zaplatiť.
          </p>
          <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: 600 }}>
                Kto platil bloček?
              </label>
              <select
                value={payerPersonOrder}
                onChange={(e) => setPayerPersonOrder(parseInt(e.target.value))}
                style={{
                  padding: '8px 12px',
                  border: '1px solid #bae6fd',
                  borderRadius: '8px',
                  fontSize: '14px',
                  maxWidth: '280px',
                  width: '100%'
                }}
              >
                {people.map((person: any) => {
                  const truncatedName = person.person_name.length > 30
                    ? person.person_name.substring(0, 30) + '...'
                    : person.person_name;
                  return (
                    <option key={person.person_order} value={person.person_order} title={person.person_name}>
                      {truncatedName}
                    </option>
                  );
                })}
              </select>
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: 600 }}>
                Kontakt pre otázky:
              </label>
              <select
                value={contactPersonOrder}
                onChange={(e) => setContactPersonOrder(parseInt(e.target.value))}
                style={{
                  padding: '8px 12px',
                  border: '1px solid #bae6fd',
                  borderRadius: '8px',
                  fontSize: '14px',
                  maxWidth: '280px',
                  width: '100%'
                }}
              >
                {people.map((person: any) => {
                  const truncatedName = person.person_name.length > 30
                    ? person.person_name.substring(0, 30) + '...'
                    : person.person_name;
                  return (
                    <option key={person.person_order} value={person.person_order} title={person.person_name}>
                      {truncatedName}
                    </option>
                  );
                })}
              </select>
            </div>
          </div>
        </div>

        {/* Status Display */}
        {isSplitProcessing && (
          <div style={{
            background: '#eef2ff',
            border: '1px solid #667eea',
            borderRadius: '12px',
            padding: '16px',
            marginBottom: '20px',
            textAlign: 'center'
          }}>
            <div style={{
              color: '#667eea',
              fontSize: '16px',
              fontWeight: 600,
              marginBottom: '8px'
            }}>
              {splitStatusMessage}
            </div>
          </div>
        )}

        {/* Error Message */}
        {errorMessage && (
          <div style={{
            background: '#fef2f2',
            border: '2px solid #ef4444',
            borderRadius: '12px',
            padding: '16px',
            marginBottom: '20px',
            color: '#dc2626',
            fontSize: '15px',
            lineHeight: '1.6',
            whiteSpace: 'pre-line'
          }}>
            <strong>⚠️ Chyba:</strong><br/>
            {errorMessage}
          </div>
        )}

        {/* Action Buttons */}
        <button
          onClick={onProcessSplit}
          disabled={isSplitProcessing}
          style={{
            width: '100%',
            minHeight: '52px',
            background: isSplitProcessing ? '#cbd5e1' : '#fb923c',
            color: '#fff',
            border: 'none',
            borderRadius: '14px',
            fontSize: '19px',
            fontWeight: 700,
            cursor: isSplitProcessing ? 'not-allowed' : 'pointer',
            marginBottom: '12px'
          }}
        >
          {isSplitProcessing ? '⏳ Spracovávam...' : '🧮 Spočítať rozdelenie'}
        </button>

        <button
          type="button"
          onClick={onBackToPeople}
          style={{
            width: '100%',
            minHeight: '52px',
            background: '#f3f4f6',
            color: '#374151',
            border: '2px solid #d1d5db',
            borderRadius: '14px',
            fontSize: '19px',
            fontWeight: 600,
            cursor: 'pointer',
            marginBottom: '100px'
          }}
        >
          ← Späť na osoby
        </button>
      </div>
    </div>
  );
};

export default SplitItemsStep;
